<script>
    import BreadcrumNav from "../../../components/breadcrum-nav.svelte";
    import { PowerTable } from "@muonw/powertable";
    import powerTableService from "../../../services/powerTable.js";
    import {
        searchAssetPropertiesCountByKind,
        searchAssetPropertiesCount,
    } from "../../asset/assets.state.svelte";
    import AssetCount from "../../asset/_components/asset-count.svelte";
    import { assets_kinds } from "../../asset/assets.api";
    import tagService from "../../tag/service.js";
    import { format, locale } from "svelte-i18n";
    import { token } from "../../user/user.state.svelte";
    import { params, goto, url } from "@roxi/routify";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    let loading = $state({ active: false });
    let data = [];
    let ptData = $state([]);

    // Routify Stores
    params;
    $goto;
    $url;

    let currentFilter = $derived($params ? $params : {});

    // SVELTE 5: Local state to hold the form values before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        asset_tag_id: ""
    });

    // Sync from URL $params to local UI state when page loads or URL changes
    $effect(() => {
        let initial = JSON.parse(JSON.stringify(currentFilter));

        // Convert comma-separated string to Array for TomSelect
        if (initial.asset__kind__in && typeof initial.asset__kind__in === "string") {
            initial.asset__kind__in = initial.asset__kind__in.split(",");
        } else {
            initial.asset__kind__in = [];
        }

        filters = {
            asset__kind__in: initial.asset__kind__in,
            asset_tag_id: initial.asset_tag_id || ""
        };
    });

    const getTags = async () => await tagService.search("v2/tag/");

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    const handleFilterChange = () => {
        let updatedFilter = { ...currentFilter };

        // Read from local state instead of raw DOM element
        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        } else {
            delete updatedFilter.asset__kind__in;
        }

        if (filters.asset_tag_id) {
            updatedFilter.asset_tag_id = filters.asset_tag_id;
        } else {
            delete updatedFilter.asset_tag_id;
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);
        $goto(window.location.pathname, cleanFilters);
    };

    const clearFilters = () => {
        if (!currentFilter.asset__kind__in && !currentFilter.asset_tag_id) {
            return;
        }
        filters = { asset__kind__in: [], asset_tag_id: "" };
        $goto(window.location.pathname, {});
    };

    const parseRows = (pageContent) => {
        const cleanParams = handleEmptyFilters(currentFilter);
        const queryStr = new URLSearchParams(cleanParams).toString();

        pageContent.forEach((row) => {
            if (row.kind) {
                const targetUrl = `/property/asset/${row.kind}${queryStr ? '?' + queryStr : ''}`;
                row.kind = `<a href="${targetUrl}">${row.kind}</a>`;
            }
        });
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "assetPropKindTable",
        rowsPerPageOptions: [5, 10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let ptInstructs = [
        {
            key: "kind",
            title: $format("list.columns.kind"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "count",
            title: $format("list.columns.amount"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
    ];

    const handleEmptyFilters = (filterObj) => {
        let cleanFilters = { ...filterObj };
        for (let [key, filter] of Object.entries(cleanFilters)) {
            if (filter === "" || filter === null || filter === undefined) {
                delete cleanFilters[key];
            }
        }
        return cleanFilters;
    };

    const getData = async (config = {}) => {
        if (!token.access_token) return [];

        config.filter = handleEmptyFilters(config.filter);
        data = await searchAssetPropertiesCountByKind(config);
        let tempPtData = [];
        for (let [key, count] of Object.entries(data)) {
            tempPtData.push({ kind: key, count: count });
        }
        tempPtData.sort((a, b) => b.count - a.count);
        ptData = tempPtData;

        loading.active = false;
        return ptData;
    };

    const getTotalCount = async (config = {}) => {
        if (!token.access_token) return 0;
        config.filter = handleEmptyFilters(config.filter);
        return await searchAssetPropertiesCount(config);
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i class="fas fa-shapes fa-xs"></i>
                    {$format("page.properties.asset_props")}
                </h1>
            </div>
            <div class="col-6">
                <BreadcrumNav
                    routes={[
                        { label: $format("list.columns.assets") },
                        { label: $format("page.properties.asset_props") },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        {#key currentFilter}
            <div class="card mt-3 card-secondary card-outline no-export">
                <div class="card-header">
                    <h4 class="card-title">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    <div class="row">
                        <div class="col-12 col-md-6 mb-3">
                            <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("components.advancedSearch.asset_kind")}
                            </label>

                            <!-- SVELTE 5 UPDATE: Replaced Select2 with TomSelect Action -->
                            <select
                                id="asset__kind__in"
                                name="asset__kind__in"
                                multiple
                                use:tomSelectAction={{
                                    options: assets_kinds,
                                    value: filters.asset__kind__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.asset__kind__in = val),
                                }}
                            ></select>
                        </div>

                        <div class="col-12 col-md-6 mb-3">
                            {#await getTags()}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>
                                <select class="form-select" name="asset_tag_id" disabled>
                                    <option value="">--</option>
                                </select>
                            {:then tags}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>

                                <!-- SVELTE 5 UPDATE: Swapped old classes for standard BS5 form-select -->
                                <select
                                    class="form-select"
                                    name="asset_tag_id"
                                    bind:value={filters.asset_tag_id}
                                >
                                    <option value="">--</option>
                                    {#each tags as opt}
                                        <option value={opt.id}>
                                            {opt.name}
                                        </option>
                                    {/each}
                                </select>
                            {/await}
                        </div>
                    </div>

                    <div class="btn-strip pt-3 mt-2 border-top">
                        <!-- SVELTE 5 UPDATE: Standard onclicks -->
                        <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                            <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                        </button>
                        <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                            <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                        </button>
                    </div>
                </div>
            </div>
        {/key}

        {#await getData({ filter: currentFilter })}
            <div class="card mt-3">
                <div class="card-body" style="min-height: 500px; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                    <div class="overlay">
                        <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                    </div>
                </div>
            </div>
        {:then success}
            <div class="row no-export">
                <div class="col-12 d-flex flex-wrap flex-md-nowrap" style="gap: 15px;">
                    {#if !loading.active}
                        <div class="card mt-3 w-100" style="flex: 3; align-self: stretch;">
                            <div class="card-header">
                                {$format("asset.properties.title")}
                            </div>
                            <div class="card-body">
                                <div class="table-responsive">
                                    <AssetCount data={ptData} field={"kind"} filter={currentFilter}/>
                                </div>
                            </div>
                        </div>
                        <div class="card mt-3 w-100" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title">
                                    {$format("asset.properties.total_amount")}
                                </div>
                            </div>
                            <div class="card-body" style="display: flex;">
                                <div class="info-box" style="box-shadow: none !important; align-self: center; justify-content: center;">
                                    {#await getTotalCount( { filter: currentFilter } )}
                                        <div class="overlay bg-light">
                                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                        </div>
                                    {:then count}
                                        <span class="number">{count}</span>
                                    {/await}
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="card mt-3 w-100" style="min-height: 500px;">
                            <div class="card-body" style="display: flex; justify-content: center;">
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    {#if !loading.active}
                        <div class="card mt-3">
                            <div class="card-header">
                                {$format("asset.properties.title")}
                            </div>
                            <div class="card-body">
                                <div class="table-responsive">
                                    <!-- Use a generic key based on the URL query rather than just kind__in -->
                                    {#key JSON.stringify(currentFilter)}
                                        <PowerTable
                                            {ptData}
                                            {ptInstructs}
                                            {ptOptions}
                                        />
                                    {/key}
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="card mt-3" style="min-height: 500px;">
                            <div class="card-body" style="display: flex; justify-content: center;">
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        {/await}
    </div>
</section>

<style>
    .number {
        font-size: 3.5em;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-content: center;
        justify-content: center;
    }

    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>